import { jsx as _jsx } from "react/jsx-runtime";
import { t } from '@aquilixshop/i18n';
import { buildReturnUrl } from '../../sections/_shared/paymentClient.js';
/**
 * Resolve what a buy-box REALLY does, from the block's setting + the store's two facts.
 *
 * This is "is the shopper in cart mode", and four different things can put them there — which is why it is
 * a shared function rather than a condition spelled out wherever someone needs the answer:
 *
 *  1. the seller picked "Ajouter au panier" / "Acheter maintenant" on the block (an explicit value wins);
 *  2. the COD app is uninstalled or inactive, so there is no on-page form to render and the buy-box degrades
 *     to the catalog CTA — this OVERRIDES an explicit `'cod'`, because an uninstalled app has no effect;
 *  3. the store takes CARDS (`checkoutStyle: 'cart'`, which the backend derives from a CARD store): the buyer
 *     fills their details AND their card at CHECKOUT, never on the product page (founder direction
 *     2026-07-19), so there is no on-page order form to render either. This too OVERRIDES an explicit
 *     `'cod'` — a choice made while the store sold cash on delivery, or copied from such a store — because a
 *     card store cannot paint it. The value is NOT rewritten: a store that sells COD again gets its form
 *     back. (Until 28/09/2026 the explicit `'cod'` won here and painted the 0026 inline card funnel. The
 *     builder had not offered that journey on a card store since 2026-07-31, and the founder's own page —
 *     `'cod'` stored from its COD days, the store taking cards since — opened « comme en COD » in the
 *     builder and painted the order form on the storefront.)
 *  4. `'auto'` (the default) on a store whose paradigm is already the cart — the same fact as 3.
 *
 * The builder reads it too: the header's "Show cart" switch locks on exactly this answer, and the buy-box
 * inspector reads a stored journey the store cannot paint the same way (`BlockInspector.storedJourney`), so
 * the editor and the storefront can never disagree about what the shopper sees.
 */
export function resolveOrderAction(rawAction, codAvailable, checkoutStyle) {
    const raw = typeof rawAction === 'string' ? rawAction : 'auto';
    if (raw === 'add-to-cart' || raw === 'buy-now')
        return raw;
    if (!codAvailable || checkoutStyle === 'cart')
        return 'add-to-cart';
    return 'cod';
}
/**
 * Does this STORE put the shopper in a cart — i.e. is its default buy-box journey a cart CTA rather than the
 * on-page COD order form?
 *
 * This is the store-wide half of {@link resolveOrderAction}: the answer for a buy-box nobody overrode. It is
 * deliberately blind to any per-page `orderAction`, because its callers are store-wide too — the header is
 * ONE document per store (chrome went global on 16/08), so a rule that read the page currently open would
 * make the same switch editable on one page and locked on another, for one and the same header.
 *
 * Cart, therefore locked: the store takes cards, or the COD app is off (with no on-page form to render,
 * every buy-box degrades to "Ajouter au panier"). Not a cart: a COD store with its app active — the shopper
 * fills the order form on the page and never needs a cart, so there the seller may hide the icon.
 */
export function storeJourneyIsCart(codEnabled, checkoutStyle) {
    return resolveOrderAction('auto', codEnabled, checkoutStyle) !== 'cod';
}
/**
 * CAN A SHOPPER OF THIS STORE END UP WITH A CART? — the header's "Show cart" question (05/10/2026).
 *
 * Only one thing writes the cart: a buy-box whose journey resolves to `'add-to-cart'`. The COD order form
 * never does, and neither does « Acheter maintenant » since 28/09/2026 (it hands its lines straight to the
 * checkout). So the answer is over EVERY buy-box the store has — the header is one document per store, and
 * the earlier page-only rule got it wrong both ways: it freed the switch from a page that proved nothing,
 * and it kept it locked on a « Buy now » page because the store "took cards", when no page added to cart.
 *
 * `journeys` is the store-wide list the backend reports ({@link BuyBoxJourney}); each stored journey is
 * resolved through the store's two facts exactly as the block itself resolves it, so `'auto'` on a card store,
 * or `'cod'` with the COD app off, count as a cart. `undefined` = nobody told us (a hand-built context, an
 * island, an older backend) → the store's default journey, which is what every such caller got before.
 * `[]` = the store has no buy-box anywhere → no cart is possible.
 */
export function shopperCanHaveCart(journeys, codEnabled, checkoutStyle) {
    if (journeys === undefined)
        return storeJourneyIsCart(codEnabled, checkoutStyle);
    return journeys.some((j) => resolveOrderAction(j.orderAction, codEnabled, checkoutStyle) === 'add-to-cart');
}
/** Alignment select → flex `align-items` for positioning fit-content buttons. */
export const ALIGN_ITEMS = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
};
/**
 * Shrine `.button` base — the full-width accent CTA shared by the order blocks: ≥47px tall, 30px inline
 * padding, weight 700, the theme button radius, centred content. `buy-buttons` spreads this then overrides
 * `inlineSize` (its width-% control) + `paddingBlock` (its vertical-padding control); `cod-form` uses it as-is
 * (a full-width "Commander" button), so the two look native to each other.
 */
export function orderCtaStyle(bg, text, uppercase) {
    return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5em',
        inlineSize: '100%',
        maxInlineSize: '100%',
        minBlockSize: '47px',
        border: '1px solid transparent',
        cursor: 'pointer',
        background: bg,
        color: text,
        fontFamily: 'inherit',
        fontWeight: 'var(--aqx-theme-fw-bold, 700)',
        lineHeight: 1.2,
        paddingBlock: '8px',
        paddingInline: '30px',
        borderRadius: 'var(--aqx-theme-radius-button, 6px)',
        textTransform: uppercase ? 'uppercase' : undefined,
        letterSpacing: uppercase ? '0.05em' : undefined,
    };
}
/**
 * The Stripe Payment Element, revealed after a CARD order + its PaymentIntent (`cs.cardIntent`). The host
 * injects the real Stripe island via `context.renderCardPayment` (ui-react stays Stripe-free); a missing
 * renderer degrades gracefully to a redirect notice. Renders nothing until a card order is placed.
 */
export function CardReveal({ cs, context, }) {
    if (!cs.cardIntent)
        return null;
    return (_jsx("div", { style: { marginTop: 'var(--aqx-theme-space-4, 16px)' }, children: context.renderCardPayment ? (context.renderCardPayment({
            clientSecret: cs.cardIntent.clientSecret,
            stripeAccountId: cs.cardIntent.stripeAccountId,
            returnUrl: buildReturnUrl(cs.cardIntent.orderId),
        })) : (_jsx("p", { role: "status", style: {
                fontSize: 'var(--aqx-theme-text-sm, 14px)',
                color: 'var(--aqx-scheme-muted)',
            }, children: t(context, 'checkout.redirecting') })) }));
}
//# sourceMappingURL=orderAction.js.map